import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type EmployeeRating,
  type EmployeeGoal,
  type EmployeeProfile,
  updateEmployeeGoal,
  updateEmployeeProfile,
} from '../../../shared/api/employee.service';
import { type CountryCode } from '../../../shared/config';
import {
  GOAL_AMOUNT_MAX,
  GOAL_AMOUNT_MIN,
  formatCurrency,
  parseAmount,
  resolveEmployeeGoalTargetAmount,
  resolveEmployeeGoalText,
  resolveEmployeeProfileName,
  resolveEmployeeShowRating,
  resolveRatingValueLabel,
} from '../../../shared/model';

type MyPageProfileEditor = {
  countryCode: string;
  goalAmountValue: string;
  goalText: string;
  isRatingVisible: boolean;
  profileName: string;
  ratingValueLabel: string;
  setIsRatingVisible: (nextValue: boolean) => void;
  updateGoalAmountValue: (nextGoalAmountValue: string) => void;
  updateGoalText: (nextGoalText: string) => void;
};

const GOAL_TEXT_MAX_LENGTH = 200;
const PREVIEW_PROGRESS_CURRENT_AMOUNT = 1000;

function parsedAmountToCurrencyValue(
  parsedAmount: number | null,
  countryCode: string,
): string | null {
  if (
    parsedAmount === null ||
    parsedAmount < GOAL_AMOUNT_MIN ||
    parsedAmount > GOAL_AMOUNT_MAX
  ) {
    return null;
  }

  return formatCurrency(parsedAmount, countryCode);
}

function resolveInitialGoalText(
  goal: EmployeeGoal | null,
  t: (key: string) => string,
) {
  const goalText = resolveEmployeeGoalText(goal);

  return goalText.length > 0 ? goalText : t('myPage.goalPlaceholder');
}

function normalizeGoalText(value: string): string {
  return value.slice(0, GOAL_TEXT_MAX_LENGTH);
}

type UseMyPageProfileEditorParams = {
  countryCode: CountryCode;
  goal?: EmployeeGoal | null;
  profile?: EmployeeProfile | null;
  rating?: EmployeeRating;
};

const EMPLOYEE_GOAL_QUERY_KEY = ['employee', 'goal'] as const;
const EMPLOYEE_PROFILE_QUERY_KEY = ['employee', 'profile'] as const;

export function useMyPageProfileEditor(
  {
    countryCode,
    goal = null,
    profile = null,
    rating,
  }: UseMyPageProfileEditorParams = {
    countryCode: '+55',
  },
): MyPageProfileEditor {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const goalSave = useMutation({
    mutationFn: updateEmployeeGoal,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: EMPLOYEE_GOAL_QUERY_KEY,
        refetchType: 'all',
      });
    },
  });
  const profileSave = useMutation({
    mutationFn: updateEmployeeProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: EMPLOYEE_PROFILE_QUERY_KEY,
        refetchType: 'all',
      });
    },
  });
  const profileName = resolveEmployeeProfileName(
    profile,
    t('myPage.previewName'),
  );
  const ratingValueLabel = resolveRatingValueLabel(rating);
  const [goalText, setGoalText] = useState(() =>
    resolveInitialGoalText(goal, t),
  );
  const [goalAmountValue, setGoalAmountValue] = useState(() =>
    formatCurrency(
      resolveEmployeeGoalTargetAmount(goal) ?? PREVIEW_PROGRESS_CURRENT_AMOUNT,
      countryCode,
    ),
  );
  const [isRatingVisible, setIsRatingVisibleState] = useState(
    resolveEmployeeShowRating(profile),
  );

  return {
    countryCode,
    goalAmountValue,
    goalText,
    isRatingVisible,
    profileName,
    ratingValueLabel,
    setIsRatingVisible: async nextValue => {
      if (profileSave.isPending) {
        return;
      }

      const previousValue = isRatingVisible;

      setIsRatingVisibleState(nextValue);
      try {
        await profileSave.mutateAsync({ showRating: nextValue });
      } catch {
        setIsRatingVisibleState(previousValue);
      }
    },
    updateGoalAmountValue: async nextGoalAmountValue => {
      if (goalSave.isPending) {
        return;
      }

      const savedGoalAmountValue = parsedAmountToCurrencyValue(
        parseAmount(nextGoalAmountValue),
        countryCode,
      );

      if (savedGoalAmountValue === null) {
        return;
      }

      const previousGoalAmountValue = goalAmountValue;
      const nextTargetAmount =
        parseAmount(savedGoalAmountValue) ?? goal?.targetAmount ?? 0;

      setGoalAmountValue(savedGoalAmountValue);
      try {
        await goalSave.mutateAsync({
          targetAmount: nextTargetAmount,
          text: goalText,
        });
      } catch {
        setGoalAmountValue(previousGoalAmountValue);
      }
    },
    updateGoalText: async nextGoalText => {
      if (goalSave.isPending) {
        return;
      }

      const normalizedGoalText = normalizeGoalText(nextGoalText);
      const previousGoalText = goalText;

      setGoalText(normalizedGoalText);
      try {
        await goalSave.mutateAsync({
          targetAmount: parseAmount(goalAmountValue) ?? goal?.targetAmount ?? 0,
          text: normalizedGoalText,
        });
      } catch {
        setGoalText(previousGoalText);
      }
    },
  };
}
